import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useDatePicker, { DatePickerHook } from "commons/hooks/input/useDatePicker";
import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { createLocalDateFromUnixMilli, createUnixMilliFromLocalDate } from "commons/utils/moment";
import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import useWorkApi from "hooks/api/private/useWorkApi";
import { useWorkOfferId } from "hooks/useId";

import { LanguageEnum } from "types/api/commons";
import { WorkBody, WorkDetails } from "types/api/work";

import { getExtraErrorsDescription } from "utils/validators";

export type WorkOfferFormType = {
    id?: string;
    date: number | null;
    enabled: boolean;
    slug: string;
    text: {
        [x in LanguageEnum.it]: {
            title: string;
            full_description: string;
            short_description: string;
        };
    };
};

export type WorkOfferFormHook = {
    formHook: FormHookType<WorkOfferFormType>;
    datePickerHookFrom: DatePickerHook;
};

function useWorkOfferForm(): WorkOfferFormHook {
    const workOfferId = useWorkOfferId();

    const { createWork, updateWork, getOneWork } = useWorkApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                enabled: {
                    type: "simple",
                    validators: [],
                },
                date: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                slug: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            title: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            full_description: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            short_description: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: WorkOfferFormType = React.useMemo(
        () => ({
            date: null,
            enabled: true,
            slug: "",
            text: {
                it: {
                    title: "",
                    full_description: "",
                    short_description: "",
                },
            },
        }),
        [],
    );

    const formHook = useForm<WorkOfferFormType>(schema, initialValues, getExtraErrorsDescription);

    const { checkErrorVisibility, showAllError, setValue, setForm, isValid, form } = formHook;

    const datePickerHookFrom = useDatePicker({
        setValue: setValue,
        checkErrorVisibility: checkErrorVisibility,
        label: "Data",
        value: form.date,
        path: "date",
    });

    const [workOfferDetails, setWorkOfferDetails] = React.useState<WorkDetails | undefined>(
        undefined,
    );

    React.useEffect(() => {
        if (workOfferId) {
            const [apiCall, abortController] = getOneWork({ id: workOfferId });

            apiCall.then(setWorkOfferDetails);

            return () => {
                abortController.abort();
            };
        }
    }, [getOneWork, workOfferId]);

    // complete form from data
    React.useEffect(() => {
        if (workOfferDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = workOfferDetails.id;
                newForm.enabled = workOfferDetails.enabled;
                newForm.date = createUnixMilliFromLocalDate(workOfferDetails.date);
                newForm.slug = workOfferDetails.slug;

                newForm.text.it.title = workOfferDetails.text.it.title;
                newForm.text.it.full_description = workOfferDetails.text.it.full_description;
                newForm.text.it.short_description = workOfferDetails.text.it.short_description;

                return newForm;
            });
        }
    }, [setForm, workOfferDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("useWorkOfferForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const body: WorkBody = {
                    date: form.date ? createLocalDateFromUnixMilli(form.date) : "",
                    enabled: form.enabled,
                    slug: form.slug,
                    text: {
                        it: {
                            title: form.text.it.title,
                            full_description: form.text.it.full_description,
                            short_description: form.text.it.short_description,
                        },
                    },
                };

                if (workOfferId) {
                    const [apiCall] = updateWork({ id: workOfferId }, body);

                    promises.push(apiCall);
                } else {
                    const [apiCall] = createWork(body);

                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, createWork, updateWork, isValid, workOfferId, form]);

    return React.useMemo(
        (): WorkOfferFormHook => ({
            datePickerHookFrom,
            formHook,
        }),
        [datePickerHookFrom, formHook],
    );
}

export default useWorkOfferForm;
